import { useCallback, useEffect, useState, Dispatch, SetStateAction } from 'react';

type SetValue<T> = Dispatch<SetStateAction<T>>;

type StorageItem<T> = {
  key: string;
  defaultValue: T;
  version?: string;
};

function deepCopy<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  return JSON.parse(JSON.stringify(obj));
}

export default function useLocalStorage<T>(storageItem: StorageItem<T>): [T, SetValue<T>] {
  const { key, defaultValue: initialValue, version = 'v1' } = storageItem;

  const readValue = useCallback((): T => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);

      if (!item) {
        return initialValue;
      }

      const parsedItem = JSON.parse(item);

      if (parsedItem && typeof parsedItem === 'object' && parsedItem.version) {
        const storedVersion = parsedItem.version;
        const storedData = parsedItem.data;

        if (storedVersion === version) {
          return deepCopy(storedData) as T;
        }

        console.warn(
          `Version mismatch for key "${key}": stored=${storedVersion}, current=${version}. Clearing outdated data.`,
        );
        window.localStorage.removeItem(key);
        return initialValue;
      }

      console.warn(`Found unversioned data for key "${key}". Clearing legacy data.`);
      window.localStorage.removeItem(key);
      return initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}": `, error);
      return initialValue;
    }
  }, [initialValue, key, version]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue: SetValue<T> = useCallback(
    (value) => {
      try {
        const newValue = value instanceof Function ? value(storedValue) : value;

        window.localStorage.setItem(
          key,
          JSON.stringify({
            version,
            data: newValue,
          }),
        );
        setStoredValue(newValue);
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}": `, error);
      }
    },
    [key, storedValue, version],
  );

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [storedValue, setValue];
}
